import {
  V2_ALLOWED_REMINDER_CHANNELS,
  V2_ALLOWED_REMINDER_EVENT_STATUS,
  V2_REMINDER_CHANNELS,
  V2_REMINDER_EVENT_STATUS,
  V2_REMINDER_FREQUENCIES,
  V2_REMINDER_TIMES_PER_DAY_OPTIONS,
  V2_WEEKDAY_KEYS,
} from "./v2Schema";

const WEEKDAY_LABELS = Object.freeze({
  sun: "Sunday",
  mon: "Monday",
  tue: "Tuesday",
  wed: "Wednesday",
  thu: "Thursday",
  fri: "Friday",
  sat: "Saturday",
});

export function validateMedicationReminder(input = {}) {
  const errors = {};
  const timesPerDay = normalizeTimesPerDay(input.times_per_day);
  const reminderTimes = normalizeReminderTimes(input, timesPerDay);

  if (!String(input.medication_name || "").trim()) {
    errors.medication_name = "Medication name is required.";
  }

  if (!V2_REMINDER_TIMES_PER_DAY_OPTIONS.includes(timesPerDay)) {
    errors.times_per_day = "Choose times per day.";
  }

  if (reminderTimes.length !== timesPerDay || reminderTimes.some((time) => !isValidTime(time))) {
    errors.reminder_times = "Enter a valid time for each reminder.";
  }

  if (!Object.values(V2_REMINDER_FREQUENCIES).includes(input.frequency)) {
    errors.frequency = "Choose a reminder frequency.";
  }

  if (!V2_ALLOWED_REMINDER_CHANNELS.includes(input.channel)) {
    errors.channel = "Choose a reminder channel.";
  }

  if (input.frequency === V2_REMINDER_FREQUENCIES.specificDays) {
    const days = normalizeWeekdays(input.specific_days);
    if (days.length === 0) {
      errors.specific_days = "Choose at least one day.";
    }
  }

  if (input.frequency === V2_REMINDER_FREQUENCIES.once && !isValidDate(input.once_date)) {
    errors.once_date = "Choose a date.";
  }

  return {
    valid: Object.keys(errors).length === 0,
    errors,
  };
}

export function prepareMedicationReminderPayload(input = {}) {
  const validation = validateMedicationReminder(input);
  if (!validation.valid) {
    return { validation, payload: null };
  }
  const timesPerDay = normalizeTimesPerDay(input.times_per_day);
  const reminderTimes = normalizeReminderTimes(input, timesPerDay);

  return {
    validation,
    payload: {
      medication_name: String(input.medication_name).trim(),
      dose: String(input.dose || "").trim(),
      times_per_day: timesPerDay,
      reminder_times: reminderTimes,
      reminder_time: reminderTimes[0],
      frequency: input.frequency,
      specific_days:
        input.frequency === V2_REMINDER_FREQUENCIES.specificDays
          ? normalizeWeekdays(input.specific_days)
          : [],
      once_date: input.frequency === V2_REMINDER_FREQUENCIES.once ? String(input.once_date).trim() : "",
      active: input.active !== false,
      channel: V2_ALLOWED_REMINDER_CHANNELS.includes(input.channel)
        ? input.channel
        : V2_REMINDER_CHANNELS.app,
    },
  };
}

export function calculateNextReminderTime(reminder = {}, now = new Date()) {
  const reminderTimes = getReminderTimes(reminder);
  if (!reminder.active || reminderTimes.length === 0) {
    return null;
  }

  const current = normalizeDate(now);
  const candidates = [];

  if (reminder.frequency === V2_REMINDER_FREQUENCIES.once) {
    if (!isValidDate(reminder.once_date)) return null;

    reminderTimes.forEach((time) => {
      const [hours, minutes] = parseTime(time);
      const onceDate = buildDateAtTime(reminder.once_date, hours, minutes);
      if (onceDate.getTime() >= current.getTime()) candidates.push(onceDate);
    });
    return firstFutureCandidate(candidates)?.toISOString() || null;
  }

  if (reminder.frequency === V2_REMINDER_FREQUENCIES.specificDays) {
    const days = normalizeWeekdays(reminder.specific_days);
    if (days.length === 0) return null;

    for (let offset = 0; offset <= 7; offset += 1) {
      const candidate = new Date(current);
      candidate.setDate(current.getDate() + offset);
      const dayKey = V2_WEEKDAY_KEYS[candidate.getDay()];
      if (!days.includes(dayKey)) continue;

      reminderTimes.forEach((time) => {
        const [hours, minutes] = parseTime(time);
        const scheduled = new Date(candidate);
        scheduled.setHours(hours, minutes, 0, 0);
        if (scheduled.getTime() >= current.getTime()) candidates.push(scheduled);
      });

      const next = firstFutureCandidate(candidates);
      if (next) {
        return next.toISOString();
      }
    }

    return null;
  }

  if (reminder.frequency === V2_REMINDER_FREQUENCIES.daily) {
    reminderTimes.forEach((time) => {
      const [hours, minutes] = parseTime(time);
      const today = new Date(current);
      today.setHours(hours, minutes, 0, 0);
      const candidate = today.getTime() >= current.getTime() ? today : addDays(today, 1);
      candidates.push(candidate);
    });
    return firstFutureCandidate(candidates)?.toISOString() || null;
  }

  return null;
}

export function getDueReminders(reminders = [], now = new Date(), windowMinutes = 1) {
  const current = normalizeDate(now);
  const windowStart = new Date(current.getTime() - windowMinutes * 60 * 1000);

  return reminders
    .flatMap((reminder) =>
      getScheduledRemindersForDate(reminder, current).map((scheduledFor) => ({
        reminder,
        scheduledFor,
      }))
    )
    .filter(({ scheduledFor }) => {
      if (!scheduledFor) return false;
      const scheduled = new Date(scheduledFor);
      return scheduled.getTime() <= current.getTime() && scheduled.getTime() >= windowStart.getTime();
    });
}

export function getScheduledReminderForDate(reminder = {}, date = new Date()) {
  return getScheduledRemindersForDate(reminder, date)[0] || null;
}

export function getScheduledRemindersForDate(reminder = {}, date = new Date()) {
  const reminderTimes = getReminderTimes(reminder);
  if (!reminder.active || reminderTimes.length === 0) {
    return [];
  }

  const current = normalizeDate(date);
  const dateKey = formatDateKey(current);
  const dayKey = V2_WEEKDAY_KEYS[current.getDay()];

  if (reminder.frequency === V2_REMINDER_FREQUENCIES.once && reminder.once_date !== dateKey) {
    return [];
  }

  if (
    reminder.frequency === V2_REMINDER_FREQUENCIES.specificDays &&
    !normalizeWeekdays(reminder.specific_days).includes(dayKey)
  ) {
    return [];
  }

  if (!Object.values(V2_REMINDER_FREQUENCIES).includes(reminder.frequency)) {
    return [];
  }

  return reminderTimes.map((time) => {
    const [hours, minutes] = parseTime(time);
    const scheduled = new Date(current);
    scheduled.setHours(hours, minutes, 0, 0);
    return scheduled.toISOString();
  });
}

export function prepareReminderEventPayload(
  reminder = {},
  status,
  {
    now = new Date(),
    scheduledFor,
    whatsappStatus = "",
    whatsappAttemptedAt = "",
    whatsappProvider = "",
    whatsappErrorMessage = "",
  } = {}
) {
  if (!reminder?.id) {
    return { ok: false, errors: { reminder_id: "Reminder was not found." }, event: null };
  }

  if (!V2_ALLOWED_REMINDER_EVENT_STATUS.includes(status)) {
    return { ok: false, errors: { status: "Choose a valid reminder status." }, event: null };
  }

  const occurredAt = normalizeDate(now).toISOString();

  const event = {
    reminder_id: reminder.id,
    medication_name_snapshot: reminder.medication_name,
    status,
    scheduled_for: scheduledFor || getScheduledReminderForDate(reminder, now) || "",
    occurred_at: occurredAt,
  };

  if (whatsappStatus) {
    event.whatsapp_status = String(whatsappStatus).trim();
    event.whatsapp_attempted_at = whatsappAttemptedAt || occurredAt;
    event.whatsapp_provider = String(whatsappProvider || "").trim();
    event.whatsapp_error_message = String(whatsappErrorMessage || "").trim();
  }

  return {
    ok: true,
    errors: {},
    event,
  };
}

export function formatNextReminder(value) {
  if (!value) return "No upcoming reminder";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "No upcoming reminder";

  return date.toLocaleString([], {
    weekday: "short",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function getReminderChannelLabel(channel) {
  if (channel === V2_REMINDER_CHANNELS.whatsapp) return "WhatsApp";
  if (channel === V2_REMINDER_CHANNELS.both) return "Both";
  return "App notification";
}

export function getFrequencyLabel(reminder = {}) {
  if (reminder.frequency === V2_REMINDER_FREQUENCIES.once) {
    return reminder.once_date ? `Once on ${reminder.once_date}` : "Once";
  }

  if (reminder.frequency === V2_REMINDER_FREQUENCIES.specificDays) {
    const labels = normalizeWeekdays(reminder.specific_days).map((day) => WEEKDAY_LABELS[day]);
    return labels.length ? labels.join(", ") : "Specific days";
  }

  return "Daily";
}

export function getReminderTimes(reminder = {}) {
  const rawTimes = Array.isArray(reminder.reminder_times) && reminder.reminder_times.length > 0
    ? reminder.reminder_times
    : [reminder.reminder_time];
  return [...new Set(rawTimes.map((time) => String(time || "").trim()).filter(isValidTime))].sort();
}

export function getReminderTimesLabel(reminder = {}) {
  const times = getReminderTimes(reminder);
  return times.length ? times.join(", ") : "No reminder times";
}

export function getTimesPerDayLabel(timesPerDay) {
  const value = normalizeTimesPerDay(timesPerDay);
  if (value === 1) return "Once daily";
  if (value === 2) return "Twice daily";
  return `${value} times daily`;
}

export function normalizeTimesPerDay(value) {
  const numeric = Number(value);
  return V2_REMINDER_TIMES_PER_DAY_OPTIONS.includes(numeric) ? numeric : 1;
}

export function normalizeWeekdays(days = []) {
  return Array.isArray(days) ? days.filter((day) => V2_WEEKDAY_KEYS.includes(day)) : [];
}

export function shouldShowWhatsappNotice(channel) {
  return channel === V2_REMINDER_CHANNELS.whatsapp || channel === V2_REMINDER_CHANNELS.both;
}

export function getReminderNotificationKey(reminderId, scheduledFor) {
  return `${reminderId}:${scheduledFor || ""}`;
}

export { V2_REMINDER_EVENT_STATUS, V2_REMINDER_FREQUENCIES, V2_WEEKDAY_KEYS };

export { V2_REMINDER_TIMES_PER_DAY_OPTIONS };

function normalizeReminderTimes(input = {}, timesPerDay = normalizeTimesPerDay(input.times_per_day)) {
  const rawTimes = Array.isArray(input.reminder_times) && input.reminder_times.length > 0
    ? [...input.reminder_times]
    : [input.reminder_time];
  if (input.reminder_time && rawTimes[0] !== input.reminder_time) {
    rawTimes[0] = input.reminder_time;
  }
  const fallback = rawTimes[0] || input.reminder_time || "";
  const times = Array.from({ length: timesPerDay }, (_, index) => String(rawTimes[index] || fallback || "").trim());
  return times;
}

function firstFutureCandidate(candidates = []) {
  return candidates.sort((a, b) => a.getTime() - b.getTime())[0] || null;
}

function addDays(date, days) {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
}

function isValidTime(value) {
  return /^([01]\d|2[0-3]):[0-5]\d$/.test(String(value || ""));
}

function isValidDate(value) {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(String(value || ""))) return false;
  const date = new Date(`${value}T00:00:00`);
  return !Number.isNaN(date.getTime());
}

function parseTime(value) {
  const [hours, minutes] = String(value).split(":").map(Number);
  return [hours, minutes];
}

function buildDateAtTime(dateKey, hours, minutes) {
  const date = new Date(`${dateKey}T00:00:00`);
  date.setHours(hours, minutes, 0, 0);
  return date;
}

function formatDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function normalizeDate(value) {
  const date = value instanceof Date ? new Date(value) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    return new Date();
  }
  return date;
}
